import * as React from 'react';

import { api } from '@api/reduxApi';
import { ArtifactWithDetails } from '@components/HighlightReelsApp/hooks/useHighlightReelsContext';
import { AlgoliaData } from '@components/shared/Search';
import { Route, StateRouter } from '@components/StateRouter';

import { HighlightArtifact, PreviewContentSlideout } from './PreviewContentSlideout';
import { previewSlideoutRouterContext } from './routerContext';

type Props = {
  artifact: HighlightArtifact;
  closeSlideout: () => void;
  reelPreviewProps?: { onAddToHighlightReel: () => void; onRemove: () => void };
  setData?: React.Dispatch<React.SetStateAction<AlgoliaData>>;
  onBack?: () => void;
  zIndex?: string;
};

const ArtifactRoute: React.FC<{ objectID: string } & Props> = ({ objectID, ...slideoutProps }) => {
  const { data: artifacts, isLoading } = api.useGetArtifactsQuery([objectID]);

  if (isLoading) {
    // Show loading state while fetching artifact data
    return (
      <PreviewContentSlideout
        {...slideoutProps}
        artifact={{
          ...slideoutProps.artifact,
          objectID,
          title: 'Loading...'
        }}
      />
    );
  }

  const artifact = artifacts?.[0];
  if (!artifact) {
    // Artifact not found - show error or fallback
    return (
      <PreviewContentSlideout
        {...slideoutProps}
        artifact={{
          ...slideoutProps.artifact,
          objectID,
          title: 'Artifact not found'
        }}
      />
    );
  }

  // Use the real artifact data loaded from the API
  return <PreviewContentSlideout {...slideoutProps} artifact={artifact as HighlightArtifact} />;
};

export const PreviewSlideoutRouter: React.FC<React.PropsWithChildren<Props>> = ({ ...slideoutProps }) => {
  return (
    <StateRouter context={previewSlideoutRouterContext} initialPath='preview'>
      <Route path='preview' element={<PreviewContentSlideout {...slideoutProps} />} />
      <Route
        path='artifact/:objectID'
        element={({ objectID }: { objectID: string }) => <ArtifactRoute objectID={objectID} {...slideoutProps} />}
      />
    </StateRouter>
  );
};
